import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import moment from 'moment';

import { findEvolvingBudgetId } from '../budgetUtils';

import PickerBulkActions from './PickerBulkActions';
import PickerFilters from './PickerFilters';
import PickerFooter from './PickerFooter';
import PickerProjectList from './PickerProjectList';
import PickerSearch from './PickerSearch';
import SavedSetsRow from './SavedSetsRow';

/**
 * Draft-then-commit project picker popover for the org summary (IB-641).
 *
 * Opens below the filter bar, dims the report area, and owns a local draft
 * ({ [eventId]: { enabled, budget } }) seeded from the live filterData. Esc /
 * outside-click / Cancel discard the draft; Apply(N) commits it via onApply.
 *
 * "Show archived" asks the parent to refetch events (with_archived=1); the
 * extra archived events flow back in through `events` and merge into the draft.
 */
export default function ProjectPicker({ events, filterData, isAdmin, savedSetsUrl, showArchived, onToggleArchived, onApply, onClose }) {
    const [draft, setDraft] = useState({});
    const [search, setSearch] = useState('');
    const [activeYear, setActiveYear] = useState(null);
    const containerRef = useRef(null);

    // Seed / extend the draft from the live events + filterData. Re-runs when the
    // events list grows (e.g. archived events arrive) so new rows get a default
    // entry without clobbering choices already made in the draft.
    useEffect(() => {
        setDraft((prev) => {
            const next = { ...prev };
            events.forEach((event) => {
                if (next[event.id]) {
                    return;
                }

                const live = filterData[event.id];
                const evolving = findEvolvingBudgetId(event);

                next[event.id] = {
                    enabled: live ? !!live.enabled : false,
                    budget: live && live.budget != null ? live.budget : evolving,
                };
            });

            return next;
        });
    }, [events, filterData]);

    useEffect(() => {
        const onKeyDown = (e) => {
            if (e.key === 'Escape') {
                onClose();
            }
        };

        const onClickOutside = (e) => {
            if (containerRef.current && !containerRef.current.contains(e.target)) {
                onClose();
            }
        };

        window.addEventListener('keydown', onKeyDown);
        document.addEventListener('mousedown', onClickOutside);

        return () => {
            window.removeEventListener('keydown', onKeyDown);
            document.removeEventListener('mousedown', onClickOutside);
        };
    }, [onClose]);

    const years = useMemo(() => {
        const set = new Set();
        events.forEach((event) => {
            if (event.start_date) {
                set.add(moment(event.start_date).year());
            }
        });

        return Array.from(set).sort((a, b) => b - a);
    }, [events]);

    const filteredEvents = useMemo(() => {
        const term = search.trim().toLowerCase();

        return events.filter((event) => {
            if (term && !(event.name || '').toLowerCase().includes(term)) {
                return false;
            }

            if (activeYear !== null && (!event.start_date || moment(event.start_date).year() !== activeYear)) {
                return false;
            }

            return true;
        });
    }, [events, search, activeYear]);

    const draftSelectedCount = useMemo(() => Object.values(draft).filter((entry) => entry?.enabled).length, [draft]);

    const toggleProject = useCallback((eventId) => {
        setDraft((prev) => ({ ...prev, [eventId]: { ...prev[eventId], enabled: !prev[eventId]?.enabled } }));
    }, []);

    const changeBudget = useCallback((eventId, budgetId) => {
        setDraft((prev) => ({ ...prev, [eventId]: { ...prev[eventId], budget: budgetId } }));
    }, []);

    const selectAllFiltered = useCallback(() => {
        const filteredIds = new Set(filteredEvents.map((e) => e.id));
        setDraft((prev) => {
            const next = { ...prev };
            filteredIds.forEach((id) => {
                next[id] = { ...next[id], enabled: true };
            });

            return next;
        });
    }, [filteredEvents]);

    const clearAll = useCallback(() => {
        setDraft((prev) => {
            const next = {};
            Object.keys(prev).forEach((id) => {
                next[id] = { ...prev[id], enabled: false };
            });

            return next;
        });
    }, []);

    // Set every selected project to its evolving (non-locked / Latest Estimate) budget.
    const setAllToLatestEstimate = useCallback(() => {
        setDraft((prev) => {
            const next = { ...prev };
            events.forEach((event) => {
                if (!next[event.id]?.enabled) {
                    return;
                }

                next[event.id] = { ...next[event.id], budget: findEvolvingBudgetId(event) };
            });

            return next;
        });
    }, [events]);

    const clearFilters = useCallback(() => {
        setActiveYear(null);
        setSearch('');
        if (showArchived) {
            onToggleArchived(false);
        }
    }, [showArchived, onToggleArchived]);

    const applySetSelection = useCallback((selection) => {
        setDraft(selection);
    }, []);

    return (
        <>
            <div className="tw-fixed tw-inset-0 tw-bg-charcoal/20 tw-z-20" aria-hidden="true" />
            <div
                ref={containerRef}
                className="tw-relative tw-z-20 tw-bg-white tw-border-2 tw-border-grey tw-rounded-lg tw-shadow-primary tw-p-4 tw-mb-4 tw-flex tw-flex-col tw-gap-3"
            >
                <PickerSearch value={search} onChange={setSearch} />

                <SavedSetsRow savedSetsUrl={savedSetsUrl} isAdmin={isAdmin} draft={draft} events={events} onApplySelection={applySetSelection} />

                <PickerFilters
                    years={years}
                    activeYear={activeYear}
                    onSelectYear={setActiveYear}
                    showArchived={showArchived}
                    onToggleArchived={() => onToggleArchived(!showArchived)}
                    onClear={clearFilters}
                />

                <PickerBulkActions onSelectAllFiltered={selectAllFiltered} onClearAll={clearAll} onSetAllToLatestEstimate={setAllToLatestEstimate} />

                <div className="tw-h-64 tw-border tw-border-grey tw-rounded-lg tw-overflow-hidden">
                    <PickerProjectList events={filteredEvents} draft={draft} onToggle={toggleProject} onChangeBudget={changeBudget} />
                </div>

                <PickerFooter selectedCount={draftSelectedCount} onApply={() => onApply(draft)} onCancel={onClose} />
            </div>
        </>
    );
}
